// 会议室订单（销售合同下的业务单据页面配置）
// 每个单据页面一个独立 JS 文件，由 src/pages/contract/index.js 统一聚合后引入
// 【P205 1.1.02.001 / 1.2.001】文案统一：「会议室预订单」→「会议室订单」
import {
  getContractRoomOrderListData,
  saveContractRoomOrderData,
  saveContractRoomOrderEditData,
  saveContractRoomOrderDeleteData
} from "../../api/api.js";

export default {
  key: "room",
  title: "会议室订单",
  short: "会议订单",
  icon: "calendar-o",
  listFn: getContractRoomOrderListData,
  saveFn: saveContractRoomOrderData,
  editFn: saveContractRoomOrderEditData, // 编辑：按 sn 更新（仅会议室订单支持）
  // 删除：此前漏挂（接口 client/orderRoomOrderDelete 一直存在），导致列表不显示删除按钮
  deleteFn: saveContractRoomOrderDeleteData,
  // 卡片标题：订单名称 · 会议室（name 为空时自动退到 会议室名 · 日期）
  titleFields: ["name", "meeting_room_name", "on_site_time"],
  // 【P95-B4 四.2.01】快捷入口「会议室订单」补齐筛选维度，与合同内列表口径一致：
  //   使用时间（滚轮日期区间）/ 时段 / 是否免费 / 会议室 +
  //   客户 / 商机 / 销售合同 / 财务合同 四个可搜索选择器（销售端 client 前缀渲染）
  filters: [
    { prop: "on_site_time", label: "使用时间", type: "daterange", beginKey: "on_site_time_begin", endKey: "on_site_time_end" },
    { prop: "time_type", label: "时段", type: "select", optionsFn: "time_type" },
    /* 【P227】新增「门店 / 会议室」二级联动筛选：
       门店用 stores_sn、会议室用 room_sn（后端 BillListService room 寄存器 filters 已支持这两个参数）。
       会议室选项由前端按「当前已选门店」过滤（filterRoomOptions），故其 optionsFn 走动态取值。 */
    { prop: "stores_sn", label: "门店", type: "select", optionsFn: "stores", placeholder: "全部门店" },
    { prop: "room_sn", label: "会议室", type: "select", optionsFn: "rooms_filtered", placeholder: "全部会议室", dependsOn: "stores_sn" },
    { prop: "is_free", label: "是否免费", type: "select",
      options: [
        { label: "否（收费）", value: 1 },
        { label: "是（免费）", value: 2 }
      ]
    },
    { prop: "customer_sn", label: "客户", type: "remote", kind: "customer", placeholder: "全部客户" },
    { prop: "opportunitie_sn", label: "商机", type: "remote", kind: "opportunity", placeholder: "全部商机" },
    { prop: "parent_sn", label: "销售合同", type: "remote", kind: "sales_contract", placeholder: "全部销售合同" },
    { prop: "contracts_sn", label: "财务合同", type: "remote", kind: "finance_contract", placeholder: "全部财务合同" }
  ],
  fields: [
    // ===== 基础信息（与 room_order_form_preview 1:1）=====
    {
      // 01 合同只读：从合同详情进入时，合同固定为当前合同，不允许改（避免串单）
      prop: "contracts_sn", label: "合同", type: "select", group: "基础信息",
      readonly: true, options: [], placeholder: "默认当前合同"
    },
    {
      /* 【P354】所属客户改为**锁定不可改**（用户 10-14 需求）。
         原注释说明「合同未关联客户时仍可点选，用于补充场景」——
         但会议订单的客户必须与上级销售合同一致，否则会出现「订单客户 ≠ 合同客户」
         的数据口径分裂（与 P353 商机同一问题）。现改为 readonly：
         红星 + 锁图标，不弹选择器；值由 contractDetailMixin.openAdd 从合同继承
         （`form.customer_sn = contract.customer_sn`，见该文件 openAdd）。 */
      prop: "customer_sn", label: "所属客户", type: "select", required: true, group: "基础信息",
      options: [], placeholder: "自动继承销售合同客户", nameKey: "customer_name",
      readonly: true
    },
    {
      /* 【P353】商机改为**继承上级销售合同、只读不可选**（用户 10-14 需求，与销售端
         client.vue 会议室订单表单口径一致）。
         ★ 本文件是 **contractDetail 的「会议室订单」新增/编辑表单**
           （合同详情 → 业务单据 → 会议室订单 → 新增），
           与 client.vue 的「新增订单」表单是两套，P348/P351 只改了后者 ⇒ 本文件被漏掉，
           用户看到的仍是有箭头、可点、带「选填 · 关联商机」旧文案的版本。
         值来源：contractDetailMixin.openAdd 已继承 contract 的 sn + name（见该文件 openAdd）。
         readonly + required：渲染红星 + 锁图标，无选择器。 */
      prop: "opportunitie_sn", label: "所属商机", type: "select", group: "基础信息",
      options: [], placeholder: "自动继承销售合同商机", nameKey: "opportunitie_name",
      readonly: true, required: true
    },
    {
      prop: "stores_sn", label: "所属门店", type: "select", required: true, group: "基础信息",
      options: [], placeholder: "请选择门店", nameKey: "stores_name"
    },
    {
      // 会议室：option value = 会议室 sn，选中后回填名称/编号（隐藏提交）
      prop: "meeting_room_sn", label: "会议室", type: "select", required: true, group: "基础信息",
      options: [], placeholder: "请选择会议室",
      fill: { meeting_room_name: "name", meeting_room_room_number: "room_number" },
      nameKey: "meeting_room_name"
    },
    { prop: "meeting_room_name", label: "会议室名称", type: "hidden" },
    { prop: "meeting_room_room_number", label: "会议室编号", type: "hidden" },

    // ===== 时间 =====
    { prop: "on_site_time", label: "使用日期", type: "date", required: true, group: "时间" },
    {
      prop: "time_type_name", label: "时段", type: "select", required: true, group: "时间",
      placeholder: "请选择时段",
      options: [
        { label: "全天", value: 1 },
        { label: "白天", value: 2 },
        { label: "上午", value: 3 },
        { label: "下午", value: 4 },
        { label: "晚上", value: 5 },
        { label: "下午+晚上", value: 7 }
      ]
    },
    { prop: "start_time", label: "使用时间", type: "range", group: "时间" },
    /* 【P97】三.3.01 人数改 TEXT：不校验数字，写了就算。
       - 列类型本就是 varchar(50)（实测 information_schema 全部 number_people 均为 varchar）
       - 后端 _contractTabRoomOrderBuild 已同步去掉 intval 与「人数必须大于 0」
       - 人数不参与价格计算、也不参与编辑变更比对（见 ClientController:24842 注释），故改 TEXT 无副作用
       - 前端 type 不是 "number" 即走 contractDetail.vue 表单渲染链末尾的纯文本 <input> 分支 */
    { prop: "number_people", label: "人数", type: "text", required: true, group: "时间", placeholder: "请输入人数（可填文字）" },

    // ===== 金额 =====
    // 【P14】是否免费：移入金额组（创建时先选是否免费再定价）
    // 免费单：销售价格默认 0、无需回款计划；创建时自动送审批（后端 _contractTabRoomOrderApprove 免费申请分支）
    {
      prop: "is_free", label: "是否免费", type: "select", group: "金额", required: true,
      placeholder: "请选择是否免费",
      options: [
        { label: "否（收费）", value: 1 },
        { label: "是（免费）", value: 2 }
      ]
    },
    // 05 市场价格为价目表定价，固定不可改（选会议室/时段后自动带出；后端同口径强制价目表值）
    { prop: "price", label: "市场价格", type: "number", readonly: true, group: "金额", placeholder: "选会议室/时段后自动带出" },
    {
      // 【P14】免费单无需填写销售价格（后端落 0 元）；非免费必填
      prop: "amount", label: "销售价格", type: "number", required: true, group: "金额", placeholder: "请输入销售价格",
      showIf: form => Number(form.is_free) !== 2
    },
    {
      prop: "discount", label: "折扣率", type: "discount", group: "金额", placeholder: "自动计算（销售/市场×10）",
      showIf: form => Number(form.is_free) !== 2
    },

    // ===== 审批 =====
    // 03 审批状态：列表接口挂载 status_name（审核中/通过/驳回）与 approval_status_name（等待审核/通过/未通过）
    //   【P108】两者均已由 BillListService::enrich 挂载；卡片展示「审核状态：xxx」标签，"等待审核"按黄色渲染
    // 卡片状态 tag 走 billMeta(status_name)，详情网格自动展示，无需表单字段（hidden 声明）
    { prop: "approval_status_name", label: "审批状态", type: "hidden" },

    // ===== 配置 =====
    {
      //【P33 修复】桌型/增值服务的 value 必须是**数据库 id**，不能是中文名。
      //  根因：老系统（与 PC 后台/统计报表）读的是 json_decode 后 array_column 的第 0 列，
      //  落库口径固定为二维 id 数组 [[248],[249]]（t_pro_table_types.id）。
      //  原实现硬编码 value="回字型" 等中文名，且 buildAddPayload 会用 join(",") 拼成
      //  逗号串提交，后端判定「非纯数字」后整段丢弃 → id=68804 落库 table_types='[]'。
      //  options 由 contractDetailMixin.loadRoomFormOptions() 从
      //  client/roomOrder/getFormData 动态注入（与 H5 会议室订单页同源，表数据变更无需改代码）。
      prop: "table_types", label: "桌型", type: "chips", required: true, group: "配置",
      options: []
    },
    {
      prop: "option_service", label: "增值服务", type: "chips", group: "配置",
      options: []
    },
    // ===== 回款计划 =====
    // 03 强校验：非免费单「必须填写」，且各行金额合计需等于销售价格（后端 _contractTabRoomOrderBuild 同口径校验）
    // 【P14】免费单隐藏（后端强制清空计划，不落脏数据）
    {
      prop: "pay_plans", label: "回款计划", type: "plans", group: "回款计划",
      requiredWhen: "notFree",
      hint: "非免费单必填：各行金额合计需等于销售价格",
      showIf: form => Number(form.is_free) !== 2
    },

    // ===== 备注 =====
    { prop: "remark", label: "备注", type: "textarea", group: "备注", placeholder: "选填" }
  ]
};



// WEBPACK FOOTER //
// ./src/pages/contract/RoomOrders.js